import CookiesKeys, { cookieStorage } from "./cookies";

const TAB_ID_KEY = "SimePortalTabId";
const ACTIVE_SESSION_MAX_AGE_MS = 12 * 60 * 60 * 1000;

export interface PortalSession {
  tabId: string;
  tenant: string | null;
  moduleId?: string | null;
  rolId?: string | null;
  portalUrl: string;
  portalName: string;
  updatedAt: number;
}

export interface PortalTarget {
  tenant?: string | null;
  moduleId?: string | null;
  rolId?: string | null;
  portalUrl?: string | null;
  portalName?: string | null;
}

const normalizeUrl = (url?: string | null) => (url || "").trim().replace(/\/$/, "");

const getStoredTenant = () => cookieStorage.getItem(CookiesKeys.Tenant);

const normalizeValue = (value?: string | null) => (value || "").trim().toUpperCase();

const safeParseSession = (value: string | null): PortalSession | null => {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as PortalSession;
    return parsed?.updatedAt ? parsed : null;
  } catch {
    return null;
  }
};

export const getPortalTabId = () => {
  let tabId = sessionStorage.getItem(TAB_ID_KEY);

  if (!tabId) {
    tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(TAB_ID_KEY, tabId);
  }

  return tabId;
};

export const getCurrentPortalUrl = () => {
  return normalizeUrl(process.env.REACT_APP_API_BASE_URL_Portal || window.location.origin);
};

export const getCurrentPortalName = () => {
  return process.env.REACT_APP_CODE_TENANT || window.location.hostname;
};

export const currentPortalOwnsStoredTenant = () => {
  const currentPortalTenant = normalizeValue(process.env.REACT_APP_CODE_TENANT);
  const storedTenant = normalizeValue(getStoredTenant());

  return !currentPortalTenant || !storedTenant || currentPortalTenant === storedTenant;
};

export const getActivePortalSession = () => {
  return safeParseSession(cookieStorage.getItem(CookiesKeys.ActivePortalSession));
};

export const isFreshPortalSession = (session: PortalSession | null) => {
  return !!session && Date.now() - session.updatedAt < ACTIVE_SESSION_MAX_AGE_MS;
};

export const setActivePortalSession = (target: PortalTarget = {}) => {
  const session: PortalSession = {
    tabId: getPortalTabId(),
    tenant: target.tenant || getStoredTenant(),
    moduleId: target.moduleId,
    rolId: target.rolId,
    portalUrl: normalizeUrl(target.portalUrl) || getCurrentPortalUrl(),
    portalName: target.portalName || getCurrentPortalName(),
    updatedAt: Date.now(),
  };

  cookieStorage.setItem(CookiesKeys.ActivePortalSession, JSON.stringify(session));
  return session;
};

export const shouldConfirmPortalTransfer = (target: PortalTarget) => {
  const activeSession = getActivePortalSession();
  const targetTenant = normalizeValue(target.tenant);
  const targetPortalUrl = normalizeUrl(target.portalUrl) || getCurrentPortalUrl();

  if (activeSession && isFreshPortalSession(activeSession) && activeSession.tabId !== getPortalTabId()) {
    const activePortalUrl = normalizeUrl(activeSession.portalUrl) || getCurrentPortalUrl();
    const activeTenant = normalizeValue(activeSession.tenant);

    return activePortalUrl !== targetPortalUrl || (!!activeTenant && !!targetTenant && activeTenant !== targetTenant);
  }

  return false;
};

export const hasSessionMovedToAnotherPortal = () => {
  const activeSession = getActivePortalSession();

  if (!isFreshPortalSession(activeSession) || activeSession?.tabId === getPortalTabId()) {
    return false;
  }

  const currentTenant = getStoredTenant();
  return (
    normalizeUrl(activeSession?.portalUrl) !== getCurrentPortalUrl() ||
    (!!currentTenant && activeSession?.tenant !== currentTenant)
  );
};
